/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Directionality} from '@angular/cdk/bidi';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {Platform} from '@angular/cdk/platform';
import {auditTime, takeUntil} from '@angular/cdk/rxjs';
import {ViewportRuler} from '@angular/cdk/scrolling';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Directive,
  ElementRef,
  forwardRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Optional,
  QueryList,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  CanColor,
  CanDisable,
  CanDisableRipple, MATERIAL_COMPATIBILITY_MODE,
  MD_RIPPLE_GLOBAL_OPTIONS,
  MdRipple,
  mixinColor,
  mixinDisabled,
  mixinDisableRipple,
  RippleGlobalOptions,
  ThemePalette,
} from '@angular/material/core';
import {fromEvent} from 'rxjs/observable/fromEvent';
import {merge} from 'rxjs/observable/merge';
import {of as observableOf} from 'rxjs/observable/of';
import {Subject} from 'rxjs/Subject';
import {MdInkBar} from '../ink-bar';
/**
 * \@docs-private
 */
export class MdTabNavBase {
/**
 * @param {?} _renderer
 * @param {?} _elementRef
 */
constructor(public _renderer: Renderer2,
public _elementRef: ElementRef) {}
}

function MdTabNavBase_tsickle_Closure_declarations() {
/** @type {?} */
MdTabNavBase.prototype._renderer;
/** @type {?} */
MdTabNavBase.prototype._elementRef;
}

export const /** @type {?} */ _MdTabNavMixinBase = mixinDisableRipple(mixinColor(MdTabNavBase, 'primary'));
/**
 * Navigation component matching the styles of the tab group header.
 * Provides anchored navigation with animated ink bar.
 */
export class MdTabNav extends _MdTabNavMixinBase implements AfterContentInit, CanColor,
    CanDisableRipple, OnDestroy {
/**
 * Subject that emits when the component has been destroyed.
 */
private _onDestroy = new Subject<void>();

  _activeLinkChanged: boolean;
  _activeLinkElement: ElementRef;

   _inkBar: MdInkBar;
/**
 * Query list of all tab links of the tab navigation.
 */
_tabLinks: QueryList<MdTabLink>;
/**
 * Background color of the tab nav.
 * @return {?}
 */
get backgroundColor(): ThemePalette { return this._backgroundColor; }
/**
 * @param {?} value
 * @return {?}
 */
set backgroundColor(value: ThemePalette) {
    let /** @type {?} */ nativeElement = this._elementRef.nativeElement;

    this._renderer.removeClass(nativeElement, `mat-background-${this.backgroundColor}`);

    if (value) {
      this._renderer.addClass(nativeElement, `mat-background-${value}`);
    }

    this._backgroundColor = value;
  }
private _backgroundColor: ThemePalette;
/**
 * Whether ripples should be disabled for all links or not.
 * @return {?}
 */
get disableRipple() { return this._disableRipple; }
/**
 * @param {?} value
 * @return {?}
 */
set disableRipple(value: boolean) {
    this._disableRipple = coerceBooleanProperty(value);
    this._setLinkDisableRipple();
  }
private _disableRipple: boolean = false;
/**
 * @param {?} renderer
 * @param {?} elementRef
 * @param {?} _dir
 * @param {?} _ngZone
 * @param {?} _changeDetectorRef
 */
constructor(renderer: Renderer2,
              elementRef: ElementRef,
private _dir: Directionality,
private _ngZone: NgZone,
private _changeDetectorRef: ChangeDetectorRef) {
    super(renderer, elementRef);
  }
/**
 * Notifies the component that the active link has been changed.
 * @param {?} element
 * @return {?}
 */
updateActiveLink(element: ElementRef) {
    this._activeLinkChanged = this._activeLinkElement != element;
    this._activeLinkElement = element;

    if (this._activeLinkChanged) {
      this._changeDetectorRef.markForCheck();
    }
  }
/**
 * @return {?}
 */
ngAfterContentInit(): void {
    this._ngZone.runOutsideAngular(() => {
      let /** @type {?} */ dirChange = this._dir ? this._dir.change : observableOf(null);
      let /** @type {?} */ resize = typeof window !== 'undefined' ?
          auditTime.call(fromEvent(window, 'resize'), 10) :
          observableOf(null);

      return takeUntil.call(merge(dirChange, resize), this._onDestroy).subscribe(() => {
        this._alignInkBar();
      });
    });

    this._setLinkDisableRipple();
  }
/**
 * Checks if the active link has been changed and, if so, will update the ink bar.
 * @return {?}
 */
ngAfterContentChecked(): void {
    if (this._activeLinkChanged) {
      this._alignInkBar();
      this._activeLinkChanged = false;
    }
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
/**
 * Aligns the ink bar to the active link.
 * @return {?}
 */
_alignInkBar(): void {
    if (this._activeLinkElement) {
      this._inkBar.alignToElement(this._activeLinkElement.nativeElement);
    }
  }
/**
 * Sets the `disableRipple` property on each link of the navigation bar.
 * @return {?}
 */
private _setLinkDisableRipple() {
    if (this._tabLinks) {
      this._tabLinks.forEach(link => link.disableRipple = this.disableRipple);
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: '[md-tab-nav-bar], [mat-tab-nav-bar]',
  inputs: ['color', 'disableRipple'],
  templateUrl: 'tab-nav-bar.html',
  styleUrls: ['tab-nav-bar.css'],
  host: {'class': 'mat-tab-nav-bar'},
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [{provide: MATERIAL_COMPATIBILITY_MODE, useValue: true}],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer2, },
{type: ElementRef, },
{type: Directionality, decorators: [{ type: Optional }, ]},
{type: NgZone, },
{type: ChangeDetectorRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_inkBar': [{ type: ViewChild, args: [MdInkBar, ] },],
'_tabLinks': [{ type: ContentChildren, args: [forwardRef(() => MdTabLink), {descendants: true}, ] },],
'backgroundColor': [{ type: Input },],
};
}

function MdTabNav_tsickle_Closure_declarations() {
/** @type {?} */
MdTabNav.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdTabNav.ctorParameters;
/** @type {?} */
MdTabNav.propDecorators;
/**
 * Subject that emits when the component has been destroyed.
 * @type {?}
 */
MdTabNav.prototype._onDestroy;
/** @type {?} */
MdTabNav.prototype._activeLinkChanged;
/** @type {?} */
MdTabNav.prototype._activeLinkElement;
/** @type {?} */
MdTabNav.prototype._inkBar;
/**
 * Query list of all tab links of the tab navigation.
 * @type {?}
 */
MdTabNav.prototype._tabLinks;
/** @type {?} */
MdTabNav.prototype._backgroundColor;
/** @type {?} */
MdTabNav.prototype._disableRipple;
/** @type {?} */
MdTabNav.prototype._dir;
/** @type {?} */
MdTabNav.prototype._ngZone;
/** @type {?} */
MdTabNav.prototype._changeDetectorRef;
}

export class MdTabLinkBase {}
export const /** @type {?} */ _MdTabLinkMixinBase = mixinDisabled(MdTabLinkBase);
/**
 * Link inside of a `md-tab-nav-bar`.
 */
export class MdTabLink extends _MdTabLinkMixinBase implements OnDestroy, CanDisable {
/**
 * Whether the tab link is active or not.
 */
private _isActive: boolean = false;
/**
 * Whether the ripples for this tab should be disabled or not.
 */
private _disableRipple: boolean = false;
/**
 * Reference to the instance of the ripple for the tab link.
 */
private _tabLinkRipple: MdRipple;
/**
 * Whether the link is active.
 * @return {?}
 */
get active(): boolean { return this._isActive; }
/**
 * @param {?} value
 * @return {?}
 */
set active(value: boolean) {
    this._isActive = value;
    if (value) {
      this._mdTabNavBar.updateActiveLink(this._elementRef);
    }
  }
/**
 * Whether ripples should be disabled or not.
 * @return {?}
 */
get disableRipple(): boolean { return this._disableRipple; }
/**
 * @param {?} value
 * @return {?}
 */
set disableRipple(value: boolean) {
    this._disableRipple = value;
    this._tabLinkRipple.disabled = this.disableRipple;
    this._tabLinkRipple._updateRippleRenderer();
  }
/**
 * \@docs-private
 * @return {?}
 */
get tabIndex(): number {
    return this.disabled ? -1 : 0;
  }
/**
 * @param {?} _mdTabNavBar
 * @param {?} _elementRef
 * @param {?} ngZone
 * @param {?} ruler
 * @param {?} platform
 * @param {?} globalOptions
 */
constructor(private _mdTabNavBar: MdTabNav,
private _elementRef: ElementRef,
              ngZone: NgZone,
              ruler: ViewportRuler,
              platform: Platform,
                globalOptions: RippleGlobalOptions) {
    super();

    // Manually create a ripple instance that uses the tab link element as trigger element.
    // Notice that the lifecycle hooks for the ripple config won't be called anymore.
    this._tabLinkRipple = new MdRipple(_elementRef, ngZone, ruler, platform, globalOptions);
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    // Manually call the ngOnDestroy lifecycle hook of the ripple instance because it won't be
    // called automatically since its instance is not created by Angular.
    this._tabLinkRipple.ngOnDestroy();
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[md-tab-link], [mat-tab-link], [mdTabLink], [matTabLink]',
  inputs: ['disabled'],
  host: {
    'class': 'mat-tab-link',
    '[attr.aria-disabled]': 'disabled.toString()',
    '[attr.tabindex]': 'tabIndex',
    '[class.mat-tab-disabled]': 'disabled'
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdTabNav, },
{type: ElementRef, },
{type: NgZone, },
{type: ViewportRuler, },
{type: Platform, },
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MD_RIPPLE_GLOBAL_OPTIONS, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'active': [{ type: Input },],
};
}

function MdTabLink_tsickle_Closure_declarations() {
/** @type {?} */
MdTabLink.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdTabLink.ctorParameters;
/** @type {?} */
MdTabLink.propDecorators;
/**
 * Whether the tab link is active or not.
 * @type {?}
 */
MdTabLink.prototype._isActive;
/**
 * Whether the ripples for this tab should be disabled or not.
 * @type {?}
 */
MdTabLink.prototype._disableRipple;
/**
 * Reference to the instance of the ripple for the tab link.
 * @type {?}
 */
MdTabLink.prototype._tabLinkRipple;
/** @type {?} */
MdTabLink.prototype._mdTabNavBar;
/** @type {?} */
MdTabLink.prototype._elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
